import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Bluebird from 'bluebird';
import TeamsContext from '@providers/Teams/TeamsContext';
import Spacer from '@components/Spacer';
import ProgressIndicator from '@components/ProgressIndicator';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import UserWorkflowCard from '@components/UserWorkflowCard';
import PaginationObserver from '@components/PaginationObserver';
import { useRouter } from 'next/router';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import { uniqBy } from 'lodash';
import NothingToDisplay from '@components/NothingToDisplay';
import { useFeatureFlag } from '@providers/FeatureFlags';
import isInIframe from '@helpers/isInIframe';
import s from './UserWorkflows.module.scss';

const PAGE_SIZE = 10;

type ThisUserWorkflow =
  & {
    numCompletedTasks: number;
    numTotalTasks: number;
  }
  & Parameters<typeof UserWorkflowCard>[0]['userWorkflow'];

type Props =
  {
    isUserView: true;
    personId?: string;
    companyId?: string;
    specifiedCreatorId: string;
    specifiedCreatorSlug: string;
  }
  | {
    isUserView: false;
    personId?: string;
    companyId?: string;
    specifiedCreatorId?: never;
    specifiedCreatorSlug?: never;
  };

const UserWorkflows: React.FC<Props> = ({ isUserView, personId, companyId, specifiedCreatorId, specifiedCreatorSlug }) => {
  const router = useRouter();
  const { creatorId } = useContext(TeamsContext);
  const currentPageRef = useRef(0);
  const [userWorkflows, setUserWorkflows] = useState<ThisUserWorkflow[]>([]);
  const [currentlyRoutingUserWorkflowId, setCurrentlyRoutingUserWorkflowId] = useState<string | null>(null);
  const [hasFetchedAllWorkflows, setHasFetchedAllWorkflows] = useState(false);
  const [hasFetchedUserWorkflows, setHasFetchedUserWorkflows] = useState(false);
  const [hasAssignedTasksByUserWorkflow, setHasAssignedTasksByUserWorkflow] = useState<Record<string, boolean | 'fetching'>>({});
  const { getData: getUserWorkflowsFn, fetchingState } = useNoodleApi(tsClient.creators.getUserWorkflows);
  const { getData: getUserWorkflowProgress } = useNoodleApi(tsClient.workflows.getUserWorkflowProgress);

  const isV3 = useFeatureFlag('user-profile-v3') && !isInIframe();

  const fetchWorkflows = useCallback(async (): Promise<void> => {
    currentPageRef.current += 1;
    let userWorkflowData: Awaited<ReturnType<typeof tsClient.creators.getUserWorkflows>> | null = null;
    if (isUserView && specifiedCreatorId) {
      const { data: workflowData } = await getUserWorkflowsFn({
        companyId,
        creatorId: specifiedCreatorId,
        page: currentPageRef.current,
        perPage: PAGE_SIZE,
        personId,
        status: [],
      });
      userWorkflowData = workflowData;
    } else if (creatorId) {
      const { data: workflowData } = await getUserWorkflowsFn({
        companyId,
        creatorId,
        page: currentPageRef.current,
        perPage: PAGE_SIZE,
        personId,
        status: [],
      });
      userWorkflowData = workflowData;
    }
    if (userWorkflowData) {
      setHasFetchedAllWorkflows(userWorkflowData.page >= userWorkflowData.numPages);
    }
    setUserWorkflows(prevUserWorkflows => uniqBy([...prevUserWorkflows, ...(userWorkflowData?.items || [])], 'id'));
  }, [creatorId, getUserWorkflowsFn, isUserView, personId, specifiedCreatorId, companyId]);

  useEffect(() => {
    fetchWorkflows();
    setHasFetchedUserWorkflows(true);
  }, [fetchWorkflows]);

  useEffect(() => {
    const needProgressIds = userWorkflows
      .filter(userWorkflow => hasAssignedTasksByUserWorkflow[userWorkflow.id] === undefined)
      .map(userWorkflow => userWorkflow.id);

    Bluebird.mapSeries(
      needProgressIds,
      async (userWorkflowId) => {
        setHasAssignedTasksByUserWorkflow(prev => ({ ...prev, [userWorkflowId]: 'fetching' }));
        const response = await getUserWorkflowProgress({ userWorkflowId });
        if (response.data) {
          const responseData = response.data;
          setHasAssignedTasksByUserWorkflow(prev => ({ ...prev, [userWorkflowId]: responseData.hasAssignedTasks }));
        }
      },
    );
  // intentionally not looking at hasAssignedTasksByUserWorkflow, only do this if the userWorkflows changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userWorkflows, getUserWorkflowProgress]);

  const handleRouteToUserWorkflowDetail = async (userWorkflowId: string): Promise<void> => {
    setCurrentlyRoutingUserWorkflowId(userWorkflowId);
    if (!isUserView) {
      router.push(getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId }));
    } else if (router.pathname === IDENTIFIERS.USER_PROFILE && specifiedCreatorSlug) {
      const urlWithUserWorkflowId = getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug: specifiedCreatorSlug, tab: 'activity', userWorkflowId });
      router.push(urlWithUserWorkflowId, undefined, { shallow: true });
    } else if (specifiedCreatorSlug) {
      if (isV3) {
        router.push(getUrl(IDENTIFIERS.USER_PROFILE_ACTIVITY_DETAILS, { creatorSlug: specifiedCreatorSlug, userWorkflowId }));
      } else {
        router.push(getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug: specifiedCreatorSlug, tab: 'activity', userWorkflowId }));
      }
    } else {
      // Should be impossible
    }
  };

  const completedWorkflows = userWorkflows.filter(userWorkflow => userWorkflow.numCompletedTasks >= userWorkflow.numTotalTasks);
  const ongoingWorkflows = userWorkflows.filter(userWorkflow => userWorkflow.numCompletedTasks < userWorkflow.numTotalTasks);

  if (ongoingWorkflows.length === 0 && completedWorkflows.length === 0 && !fetchingState.isFetching) {
    return (
      <NothingToDisplay isBordered />
    );
  }

  if (fetchingState.isFetching && userWorkflows.length === 0) {
    return (
      <>
        <Spacer size={48} />
        <ProgressIndicator isCentered />
      </>
    );
  }

  return (
    <div className={s.userWorkflows__wrapper}>
      {completedWorkflows.length > 0 && (
        <details className={s.completedWorkflows}>
          <summary>Completed Workflows <span>{completedWorkflows.length}</span></summary>
          <section className={s.userWorkflows__list} style={isUserView && { flexDirection: "column" } || undefined}>
            {completedWorkflows.map(userWorkflow => (
              <UserWorkflowCard
                key={userWorkflow.id}
                isCreatorView={!isUserView}
                userWorkflow={userWorkflow}
                onClick={handleRouteToUserWorkflowDetail}
                hasAssignedTasks={hasAssignedTasksByUserWorkflow[userWorkflow.id] ?? 'fetching'}
                isRouting={currentlyRoutingUserWorkflowId === userWorkflow.id}
              />
            ))}
          </section>
        </details>
      )}
      {ongoingWorkflows.length > 0 && (
        <section className={s.userWorkflows__list}>
          {ongoingWorkflows.map(userWorkflow => (
            <UserWorkflowCard
              key={userWorkflow.id}
              isCreatorView={!isUserView}
              userWorkflow={userWorkflow}
              onClick={handleRouteToUserWorkflowDetail}
              hasAssignedTasks={hasAssignedTasksByUserWorkflow[userWorkflow.id] ?? 'fetching'}
              isRouting={currentlyRoutingUserWorkflowId === userWorkflow.id}
            />
          ))}
        </section>
      )}
      {ongoingWorkflows.length > 0 && !hasFetchedAllWorkflows && <PaginationObserver isDisabled={!hasFetchedUserWorkflows} loadFn={fetchWorkflows} />}
    </div>
  );
};

export default UserWorkflows;
